import { useState } from "react";
import { Button, SelectField, TextField } from "@/components/ui/FormControls";
import { api } from "@/lib/api";

export interface DataConnection {
  id: string;
  name: string;
  sourceType: "standard_file" | "meter" | "erp" | "supplier";
  cadence: "manual" | "daily" | "monthly";
  status: "active" | "paused";
  lastSyncedAt: string | null;
  nextSyncAt: string | null;
  createdAt: string;
}

interface DataConnectionsPanelProps {
  projectId: string;
  connections: DataConnection[];
  onChanged: () => void;
}

const SOURCE_LABEL: Record<DataConnection["sourceType"], string> = { standard_file: "표준 CSV 및 XLSX", meter: "계측 데이터", erp: "ERP", supplier: "공급사 자료" };
const CADENCE_LABEL: Record<DataConnection["cadence"], string> = { manual: "필요할 때 업로드", daily: "매일", monthly: "매월" };

function dateText(value: string | null) {
  return value ? new Date(value).toLocaleString("ko-KR", { dateStyle: "medium", timeStyle: "short" }) : "아직 없음";
}

export function DataConnectionsPanel({ projectId, connections, onChanged }: DataConnectionsPanelProps) {
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState<DataConnection["sourceType"]>("standard_file");
  const [cadence, setCadence] = useState<DataConnection["cadence"]>("monthly");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/workspace/projects/${projectId}/connections`, { method: "POST", json: { name, sourceType, cadence } });
      setName("");
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "데이터 연결을 만들지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(connection: DataConnection) {
    try {
      await api(`/workspace/projects/${projectId}/connections/${connection.id}`, { method: "PATCH", json: { status: connection.status === "active" ? "paused" : "active" } });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "데이터 연결 상태를 바꾸지 못했습니다.");
    }
  }

  return (
    <section className="workspace-card">
      <div className="workspace-card-head">
        <div><h2 className="workspace-card-title">데이터 연결 <span className="workspace-count">{connections.length}개</span></h2><p className="workspace-card-desc">반복적으로 들어오는 자료의 출처와 업로드 주기를 등록합니다. 현재는 표준 파일 연결부터 사용할 수 있습니다.</p></div>
      </div>
      <div className="workspace-connection-list">
        {connections.length === 0 ? <p className="workspace-empty">등록된 연결이 없습니다. 아래에서 첫 연결을 추가해 주세요.</p> : connections.map((connection) => (
          <div className="workspace-connection-row" key={connection.id}>
            <div><strong>{connection.name}</strong><span>{SOURCE_LABEL[connection.sourceType]} | {CADENCE_LABEL[connection.cadence]}</span><span>최근 동기화 {dateText(connection.lastSyncedAt)} | 다음 예정 {dateText(connection.nextSyncAt)}</span></div>
            <div className="workspace-actions"><span className={connection.status === "active" ? "badge-accent" : "badge-muted"}>{connection.status === "active" ? "사용 중" : "일시 중지"}</span><Button size="compact" onClick={() => void toggle(connection)}>{connection.status === "active" ? "일시 중지" : "다시 사용"}</Button></div>
          </div>
        ))}
      </div>
      <div className="workspace-form-grid workspace-connection-form">
        <TextField label="연결 이름" placeholder="예: 울산 전력 계측자료" value={name} onChange={(event) => setName(event.target.value)} />
        <SelectField label="자료 출처" value={sourceType} onChange={(event) => setSourceType(event.target.value as DataConnection["sourceType"])}>{Object.entries(SOURCE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</SelectField>
        <SelectField label="업로드 주기" value={cadence} onChange={(event) => setCadence(event.target.value as DataConnection["cadence"])}>{Object.entries(CADENCE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</SelectField>
        <div className="workspace-actions workspace-actions-end"><Button variant="primary" disabled={busy || !name.trim()} onClick={() => void create()}>{busy ? "추가 중" : "연결 추가"}</Button></div>
      </div>
      {error && <p className="note note-danger text-caption" role="alert">{error}</p>}
    </section>
  );
}
